/**
 * 외부 앱/SDK가 주입하는 변수명 목록
 * Safari: "Can't find variable: X" / Chrome/Firefox: "X is not defined" 형태로 에러 발생
 *
 * 이 변수들은 우리 코드에 없고, 외부 앱이 웹뷰에 주입하는 스크립트에서 참조함
 */
const EXTERNAL_INJECTED_VARIABLES = [
  'gmo', // Google Mobile Optimization - 구글 브라우저 플러그인
  'zaloJSV2', // Zalo (베트남 메신저) 앱 내 웹뷰에서 주입
  // 아래 변수들은 2026-02-22 주간 Sentry 리포트에서 발견
  // 각 변수의 출처와 Sentry 이슈 링크를 기록해두었으므로, 추후 필터 해제 검토 시 참고
  'knsb', // 한국 은행 보안 플러그인 (Korean banking security plugin) - 은행 앱 웹뷰에서 주입
  'EmptyRanges', // PDF 뷰어 / 문서 플러그인이 주입 - https://zep-us.sentry.io/issues/WEB-APP-111 (17u), https://zep-us.sentry.io/issues/QUIZ-APP-1VH (132u)
  'swbrowser', // 삼성 인터넷 브라우저 WebView 주입 - https://zep-us.sentry.io/issues/QUIZ-APP-65 (254u)
  'detectAds', // 광고 차단 확장 프로그램이 주입하는 감지 스크립트 - https://zep-us.sentry.io/issues/QUIZ-APP-37Z (34u)
  'unSelectAll', // 문서 뷰어 플러그인이 주입 - https://zep-us.sentry.io/issues/WEB-APP-1CY (19u, 1,111 events)
  'isReCreate', // 외부 앱 웹뷰에서 주입되는 스크립트 변수 - https://zep-us.sentry.io/issues/QUIZ-APP-1VB (79u)
  // 아래 변수들은 2026-03-26 주간 Sentry 리포트에서 발견
  'canHistoryBack', // 외부 앱 웹뷰 히스토리 제어 - https://zep-us.sentry.io/issues/QUIZ-APP-1GW, https://zep-us.sentry.io/issues/WEB-APP-T5
  'memno', // 외부 앱 웹뷰 주입 변수 - https://zep-us.sentry.io/issues/QUIZ-APP-6YF
  'shakehot', // 외부 앱 웹뷰 주입 변수 - https://zep-us.sentry.io/issues/QUIZ-APP-6YY
  'CONFIG', // 외부 앱 웹뷰 설정 변수 - https://zep-us.sentry.io/issues/QUIZ-APP-6XG
  'video', // 외부 앱 웹뷰 주입 변수 - https://zep-us.sentry.io/issues/WEB-APP-1M9
  // 아래 변수들은 2026-03-30 주간 Sentry 리포트에서 발견
  'ucConfig', // UC Browser 설정 변수 - https://zep-us.sentry.io/issues/QUIZ-APP-66 (78u, 3,847 events)
  // 아래 변수들은 2026-06-26 주간 Sentry 리포트에서 발견 (인앱 브라우저 / 읽기보조 확장 / 호스트 페이지 전역)
  // ⚠️ 이 변수들은 패턴에 있어도 hint.originalException 이 null 이라 매칭되지 않던 bypass 가 있었음
  //    (getEventErrorMessage fallback 으로 함께 해결)
  'LIDNotify', // 인앱 브라우저(LINE 등) 알림 브릿지 주입 - https://zep-us.sentry.io/issues/WEB-APP-1RZ, https://zep-us.sentry.io/issues/AUTH-APP-28
  'LIDNotifyId', // 인앱 브라우저 알림 브릿지 주입 - https://zep-us.sentry.io/issues/AUTH-APP-24
  '__doPostBack', // ASP.NET 호스트 페이지/프록시 주입 전역 - https://zep-us.sentry.io/issues/QUIZ-APP-77S
  'tapAt', // 읽기보조/스크린리더 확장 주입 - https://zep-us.sentry.io/issues/PLAY-APP-AY1
  'removeHighlight', // 읽기보조 확장 주입 - https://zep-us.sentry.io/issues/PLAY-APP-AXZ
  'selectwords', // 읽기보조/번역 확장 주입 - https://zep-us.sentry.io/issues/PLAY-APP-AXX
  'viewUserInfo', // 외부 호스트 페이지 주입 전역 - https://zep-us.sentry.io/issues/PLAY-APP-AVX
  'fn_version', // 외부 호스트 페이지 주입 전역 - https://zep-us.sentry.io/issues/PLAY-APP-AVW
  'goLeftPage', // 외부 호스트 페이지 주입 전역 - https://zep-us.sentry.io/issues/WEB-APP-1PR
  'dirImg', // 외부 호스트 페이지 주입 전역 - https://zep-us.sentry.io/issues/QUIZ-APP-7CS
] as const;

/**
 * 브라우저 플러그인/확장 프로그램 관련 에러
 */
export const BROWSER_PLUGIN_ERROR_PATTERNS = [
  // Google AdSense 중복 push 에러 - DOM에 이미 광고가 로드된 경우 발생
  "adsbygoogle.push() error: All 'ins' elements in the DOM with class=adsbygoogle already have ads in them",

  /**
   * 외부 앱/SDK 주입 스크립트 에러
   * - 외부 앱(Zalo, 구글 등)이 웹뷰에 스크립트를 주입
   * - 해당 스크립트가 정의되지 않은 변수를 참조하면 발생
   * - 우리 코드와 무관한 외부 스크립트 에러
   *
   * 브라우저별 에러 메시지 형식:
   * - Safari: "Can't find variable: zaloJSV2"
   * - Chrome/Firefox: "zaloJSV2 is not defined"
   *
   * Refs:
   * - https://zep-us.sentry.io/issues/7043782557 (zaloJSV2)
   * - https://stackoverflow.com/questions/79434372/sentry-errors-variable-not-found-gmo
   */
  new RegExp(`Can't find variable: (${EXTERNAL_INJECTED_VARIABLES.join('|')})`),
  new RegExp(`(${EXTERNAL_INJECTED_VARIABLES.join('|')}) is not defined`),

  /**
   * 크로스 오리진 스크립트 에러
   * - 외부 서드파티 스크립트에서 발생한 에러는 보안 정책에 의해 "Script error."로만 표시됨
   * - 스택트레이스 정보도 없어 디버깅 불가능
   * - 우리 코드와 무관한 외부 스크립트 에러
   *
   * Sentry Issues: QUIZ-APP-38, PLAY-APP-7WM, WEB-APP-14J 등
   */
  'Script error.',
] as const;

/**
 * 모바일 브라우저 관련 에러
 */
export const MOBILE_BROWSER_ERROR_PATTERNS = [
  // https://zep-us.sentry.io/share/issue/af35bb21a91c4e0484c1d009811542ee/
  // 모바일 사파리 이슈
  /Object\.prototype\.hasOwnProperty\.call\(.+,"telephone"\)/,

  // 모바일에서 shadcn ui의 HoverCardTrigger 클릭 시 에러가 나는데 동작엔 이상이 없기에 무시합니다.
  'Unable to preventDefault inside passive event listener invocation',
] as const;

/**
 * Whale 브라우저 관련 에러
 */
export const WHALE_BROWSER_ERROR_PATTERNS = [
  /**
   * iOS에서 웨일 브라우저를 사용하는 경우, SecurityError: Blocked a frame with origin "https://zep.us"라는 에러가 발생
   * 웨일 4.x 미만의 브라우저를 사용할 때, 외부 서비스로 요청 시 발생하는 이슈인데, 실제 서비스 사용에는 문제가 없는 것으로 보입니다.
   * Ref1: https://forum.whale.naver.com/topic/56329/?tab=comments#comment-135932
   * Ref2: https://zep-us.sentry.io/issues/6010813111/
   * Ref3: https://zep-us.sentry.io/issues/6192959312 (quiz-app)
   */
  'Blocked a frame with origin',
] as const;

/**
 * IndexedDB 관련 에러
 *
 * 주의: DOMException의 경우 error.name과 error.message가 분리되어 있습니다.
 * 예: name='UnknownError', message='Database deleted by request of the user'
 * Sentry UI에서는 'UnknownError: Database deleted by request of the user'로 표시되지만,
 * hint.originalException.message에는 'Database deleted by request of the user'만 들어있습니다.
 * 따라서 패턴은 message 부분만 포함해야 합니다.
 */
/**
 * 브라우저 엔진 내부 에러
 * 브라우저 엔진(Gecko, WebKit, Blink)의 내부 동작이나 환경에 의해 발생하는 에러들로,
 * 서비스 로직과 무관합니다.
 *
 * Added: 2026-02-22 주간 Sentry 노이즈 리포트
 *
 * ⚠️ 필터 해제 검토 기준:
 * - 특정 패턴의 이슈가 급증(escalating)하면서 first-party 스택트레이스가 포함된 경우
 * - 새로운 기능 출시 후 해당 패턴의 에러가 실제 버그와 연관된 경우
 */
export const BROWSER_ENGINE_ERROR_PATTERNS = [
  /**
   * ResizeObserver 루프 에러
   * - Chrome/모든 브라우저에서 발생하는 무해한 에러
   * - DOM 요소 크기 변경 시 관찰자 콜백이 루프를 형성하지만 실제 문제 없음
   * - W3C 스펙상 정상적인 동작이며 브라우저가 자동으로 다음 프레임에서 재시도
   * - Ref: https://developer.mozilla.org/en-US/docs/Web/API/ResizeObserver#observation_errors
   *
   * Sentry Issues:
   * - https://zep-us.sentry.io/issues/PLAY-APP-8TP (2,016 users, play-app)
   * - https://zep-us.sentry.io/issues/QUIZ-APP-49H (88 users, quiz-app)
   * - https://zep-us.sentry.io/issues/PLAY-APP-8WZ (67 users, play-app)
   */
  'ResizeObserver loop',

  /**
   * 비정형 Promise rejection
   * - 실제 Error 객체가 아닌 값(string, undefined, null 등)이 reject된 경우
   * - Sentry가 "Non-Error promise rejection captured with value: ..."로 래핑
   * - 에러 메시지, 스택트레이스 등 유용한 디버깅 정보가 전혀 없음
   * - 주로 서드파티 라이브러리나 브라우저 내부 동작에서 발생
   *
   * Sentry Issues:
   * - https://zep-us.sentry.io/issues/PLAY-APP-7 (1,066 users, 8,133 events, play-app)
   * - https://zep-us.sentry.io/issues/WEB-APP-A (283 users, 1,826 events, web-app)
   * - https://zep-us.sentry.io/issues/QUIZ-APP-21A (5,743 users, quiz-app)
   * - https://zep-us.sentry.io/issues/QUIZ-APP-1M2 (3,207 users, quiz-app)
   */
  'Non-Error promise rejection captured',
  'Non-Error exception captured',

  /**
   * Storage 용량 초과 에러
   * - 사용자 브라우저의 localStorage/sessionStorage/IndexedDB 저장 공간 부족
   * - 특히 Private Browsing 모드에서 저장 용량이 제한되어 빈번히 발생
   * - 앱 크래시를 유발하지 않으며, 사용자 환경 문제
   *
   * Sentry Issues:
   * - https://zep-us.sentry.io/issues/PLAY-APP-76F (63 users, play-app)
   * - https://zep-us.sentry.io/issues/WEB-APP-P6 (54 users, web-app)
   * - https://zep-us.sentry.io/issues/WEB-APP-BY (38 users, web-app)
   */
  'QuotaExceededError',

  /**
   * Firefox (Gecko) 내부 에러
   * - Firefox 브라우저 엔진 내부에서 발생하는 에러들
   * - NS_ERROR_NOT_INITIALIZED: 초기화되지 않은 Gecko 내부 객체 접근
   * - NS_ERROR_FAILURE: Gecko 내부 작업 실패
   * - NS_ERROR_FILE_CORRUPTED: IndexedDB 파일 손상 (Firefox 전용)
   * - 모두 Firefox 특유의 에러로 우리 코드와 무관
   * - Ref: https://developer.mozilla.org/en-US/docs/Mozilla/Errors
   *
   * Sentry Issues:
   * - https://zep-us.sentry.io/issues/PLAY-APP-6NX (68 users, play-app)
   * - https://zep-us.sentry.io/issues/WEB-APP-SE (21 users, web-app)
   * - https://zep-us.sentry.io/issues/WEB-APP-ND (8 users, web-app)
   */
  'NS_ERROR_NOT_INITIALIZED',
  'NS_ERROR_FAILURE',
  'NS_ERROR_FILE_CORRUPTED',

  /**
   * AudioContext/InvalidStateError
   * - 이미 닫힌(closed) AudioContext에 대해 suspend/resume 시도 시 발생
   * - 브라우저 탭 전환, 페이지 이탈 시 AudioContext가 먼저 close되고
   *   이후 cleanup 코드가 실행되면서 발생하는 타이밍 이슈
   * - play-app의 Phaser 게임 엔진에서 주로 발생
   *
   * ⚠️ 주의: 'InvalidStateError'는 다소 넓은 패턴이므로,
   *    AudioContext 외 다른 InvalidStateError가 중요해지면 패턴을 세분화할 것
   *
   * Sentry Issues:
   * - https://zep-us.sentry.io/issues/PLAY-APP-98E (19 users, play-app)
   * - https://zep-us.sentry.io/issues/WEB-APP-DH (15 users, web-app)
   */
  'InvalidStateError',
] as const;

export const INDEXEDDB_ERROR_PATTERNS = [
  /**
   * IndexedDB 데이터베이스가 삭제된 경우 발생하는 에러
   * 해당 에러는 사용자가 브라우저 데이터를 삭제하거나, 저장공간 부족으로 기기에서 데이터가 자동으로 삭제되는 등
   * 접근하려는 database가 indexedDB에 존재하지 않는 경우 발생합니다.
   * 해당 에러는 앱 크래시를 유발하지 않는 것으로 보여 Sentry alert에서 무시합니다.
   * Sentry Issue: https://zep-us.sentry.io/issues/6557062283
   */
  'Database deleted by request of the user',

  /**
   * IndexedDB 서버 내부 에러
   * IndexedDB 서버 내부 에러가 발생하는 경우 발생하는 에러
   * 해당 에러는 앱 크래시를 유발하지 않는 것으로 보여 Sentry alert에서 무시합니다.
   * Ref: https://bugs.webkit.org/show_bug.cgi?id=197050
   *
   * 발생 조건:
   * 앱이 백그라운드로 전환됨
   * iOS가 메모리 관리를 위해 IndexedDB 프로세스 종료
   * 앱이 다시 포그라운드로 돌아옴
   * IndexedDB 연결이 끊어진 상태에서 트랜잭션 시도
   * 💥 에러 발생
   * WebKit 버그 리포트에서 확인된 사항:
   * iOS 12.2부터 지속적으로 발생 (2019년~현재)
   * iOS 17.4, 18.x에서도 여전히 발생
   * 메모리 부족 시 더 빈번히 발생
   * Apple이 완전히 해결하지 못한 장기 버그
   */
  'An internal error was encountered in the Indexed Database server',
] as const;

/**
 * 서드파티 SDK/확장 프로그램 에러
 * 우리 코드와 무관한 외부 SDK나 브라우저 확장 프로그램에서 발생하는 에러
 *
 * Added: 2026-03-26 주간 Sentry 노이즈 리포트
 */
export const THIRD_PARTY_SDK_ERROR_PATTERNS = [
  /**
   * MetaMask 브라우저 확장 프로그램 연결 실패
   * - MetaMask가 설치되어 있지만 연결이 안 되는 경우
   * - 우리 서비스는 MetaMask 연결을 필수로 하지 않으므로 노이즈
   *
   * Sentry Issues:
   * - https://zep-us.sentry.io/issues/PLAY-APP-6YA (327 users, play-app)
   * - https://zep-us.sentry.io/issues/QUIZ-APP-2EJ (148 users, quiz-app)
   * - https://zep-us.sentry.io/issues/WEB-APP-14J (64 users, web-app)
   * - https://zep-us.sentry.io/issues/AUTH-APP-J (31 users, auth-app)
   */
  'Failed to connect to MetaMask',

  /**
   * HyperDX/OpenTelemetry SDK 에러
   * - Observability SDK(HyperDX)의 데이터 전송 실패
   * - 모니터링 SDK 자체의 에러이므로 서비스 로직과 무관
   *
   * Sentry Issues:
   * - https://zep-us.sentry.io/issues/PLAY-APP-7WM (1,054 users, play-app)
   * - https://zep-us.sentry.io/issues/PLAY-APP-7W8 (747 users, play-app)
   */
  'OTLPExporterError',

  /**
   * 브라우저 확장 프로그램 메시징 에러
   * - 확장 프로그램이 존재하지 않는 탭에 메시지를 보내려 할 때 발생
   * - Chrome runtime.sendMessage API의 알려진 에러
   *
   * Sentry Issues:
   * - https://zep-us.sentry.io/issues/QUIZ-APP-DC (15 users, quiz-app)
   * - https://zep-us.sentry.io/issues/WEB-APP-106 (18 users, web-app)
   * - https://zep-us.sentry.io/issues/AUTH-APP-19 (2 users, auth-app)
   */
  'Invalid call to runtime.sendMessage(). Tab not found.',

  /**
   * Talisman 지갑 확장 프로그램 설정 에러
   * - Talisman 확장이 설치되었으나 온보딩이 완료되지 않은 상태
   *
   * Sentry Issue:
   * - https://zep-us.sentry.io/issues/QUIZ-APP-32M (6 users, quiz-app)
   */
  'Talisman extension has not been configured yet',

  /**
   * 지갑 확장 provider 주입 충돌 (r.ethereum.setExternalProvider)
   * - 지갑 확장이 주입한 provider 객체에 없는 메서드를 호출하며 발생
   *
   * Sentry Issue:
   * - https://zep-us.sentry.io/issues/QUIZ-APP-7EX
   */
  'setExternalProvider is not a function',
] as const;

/**
 * 사용자 의도적 액션에 의한 에러
 * 사용자가 직접 취소/거부한 동작에서 발생하는 에러로, 버그가 아님
 *
 * Added: 2026-03-26 주간 Sentry 노이즈 리포트
 *
 * ⚠️ NotAllowedError, Requested device not found, getDisplayMedia not supported,
 *    Timeout starting video source는 whitelist — 센트리 모니터링 유지
 */
export const USER_ACTION_ERROR_PATTERNS = [
  /**
   * 공유 취소 에러
   * - 사용자가 OS 공유 다이얼로그에서 취소 버튼을 눌렀을 때 발생
   * - Web Share API의 정상적인 취소 동작
   *
   * Sentry Issues:
   * - https://zep-us.sentry.io/issues/PLAY-APP-1K (240 users, play-app)
   * - https://zep-us.sentry.io/issues/PLAY-APP-1Q (135 users, play-app)
   */
  'Share canceled',
  'Abort due to cancellation of share',

  /**
   * 결제 취소 에러
   * - 사용자가 결제 과정에서 직접 취소한 경우
   *
   * Sentry Issues:
   * - https://zep-us.sentry.io/issues/QUIZ-APP-49G (75 users, quiz-app)
   * - https://zep-us.sentry.io/issues/QUIZ-APP-49H (95 users, quiz-app)
   * - https://zep-us.sentry.io/issues/QUIZ-APP-6KY (17 users, quiz-app)
   * - https://zep-us.sentry.io/issues/QUIZ-APP-6YX (1 user, quiz-app)
   */
  'Payment is cancelled by the user',
  '취소되었습니다',
  '사용자가 결제를 취소하였습니다',

  /**
   * 클립보드 읽기 권한 거부
   * - 사용자가 클립보드 접근을 거부했을 때 발생
   *
   * Sentry Issue:
   * - https://zep-us.sentry.io/issues/QUIZ-APP-1K7 (39 users, quiz-app)
   */
  'Read permission denied',

  /**
   * Next.js 라우트 렌더링 취소
   * - 사용자가 페이지 이동 중 다른 액션을 수행하여 렌더링이 취소된 경우
   *
   * Sentry Issue:
   * - https://zep-us.sentry.io/issues/AUTH-APP-H (5 users, auth-app)
   */
  'Cancel rendering route',
] as const;

/**
 * Android WebView / iOS WKWebView 에러
 * 네이티브 앱의 WebView 브릿지 관련 에러로, 웹 코드와 무관
 *
 * Added: 2026-03-26 주간 Sentry 노이즈 리포트
 */
export const WEBVIEW_ERROR_PATTERNS = [
  /**
   * Android WebView 브릿지 에러
   * - 네이티브 앱의 WebView가 JavaScript 브릿지 객체를 해제한 후
   *   웹 코드가 해당 객체에 접근하려 할 때 발생
   * - Android WebView의 생명주기 이슈로, 앱 코드 문제 아님
   *
   * Sentry Issues:
   * - https://zep-us.sentry.io/issues/QUIZ-APP-5WJ (2,826 users, quiz-app)
   * - https://zep-us.sentry.io/issues/QUIZ-APP-3TG (86 users, quiz-app)
   * - https://zep-us.sentry.io/issues/WEB-APP-1GA (98 users, web-app)
   * - https://zep-us.sentry.io/issues/QUIZ-APP-3TK (539 users, quiz-app)
   */
  'Java object is gone',
  'Java bridge method invocation error',

  /**
   * iOS WKWebView 에러
   * - WKWebView가 해제(deallocated)된 후 메시지가 전달되려 할 때 발생
   * - iOS의 WKWebView 생명주기 이슈
   *
   * Sentry Issues:
   * - https://zep-us.sentry.io/issues/QUIZ-APP-6JE (10 users, quiz-app)
   * - https://zep-us.sentry.io/issues/WEB-APP-1HY (2 users, web-app)
   */
  'The WKWebView was deallocated',

  /**
   * iOS WKWebView 메시지 핸들러 에러
   * - window.webkit.messageHandlers가 정의되지 않은 환경에서 접근 시 발생
   * - iOS WKWebView 외부 환경(일반 브라우저)에서 접근하거나, 커스텀 프로토콜이 등록되지 않은 경우
   *
   * Sentry Issues:
   * - https://zep-us.sentry.io/issues/QUIZ-APP-6YZ (1 user, quiz-app)
   * - https://zep-us.sentry.io/issues/WEB-APP-1MN (1 user, web-app)
   * - https://zep-us.sentry.io/issues/WEB-APP-1MR (1 user, web-app)
   */
  'window.webkit.messageHandlers',

  /**
   * Android WebView 브릿지 메서드 호출 에러
   * - `Error invoking <methodName>: Method not found` 형태
   * - 네이티브 앱의 @JavascriptInterface 메서드가 없거나 호출 실패 시 발생
   *
   * Sentry Issues:
   * - https://zep-us.sentry.io/issues/AUTH-APP-G (145 users, auth-app)
   * - https://zep-us.sentry.io/issues/WEB-APP-1GA (100 users, web-app)
   */
  'Error invoking',
] as const;
